import React from "react";
import useApi from "../../hooks/useApi";
import {
  hoje,
  primeiroDiaDoMes,
  ultimoDiaDoMes,
} from "../../helpers/mesesHelper";
import { AppContext } from "../../contexts/AppContext.jsx";
import CobrancaApiClient from "../../api/cobranca.js";
import Card from "../../components/page/Card.jsx";
import ConsultarCobrancasCards from "./ConsultarCobrancas/ConsultarCobrancasCards.jsx";
import ConsultarCobrancasFiltros from "./ConsultarCobrancas/ConsultarCobrancasFiltros.jsx";
import DownloadMassaModal from "./ConsultarCobrancas/DownloadMassaModal.jsx";
import CommonSwal from "../../components/Swal/commonSwal.jsx";
import ErrorSwal from "../../components/Swal/errorSwal.jsx";
import EnviarEmailModal from "./ConsultarCobrancas/EnviarEmailModal.jsx";
import Validation from "../../helpers/Validation.js";
import InformarPagamentoModal from "./ConsultarCobrancas/InformarPagamentoModal.jsx";
import VisualizarCobrancaModal from "./ConsultarCobrancas/VisualizarCobrancaModal.jsx";
import OptionsSwal from "../../components/Swal/optionsSwal.jsx";
import SolicitacaoCobrancaSwalHtml from "./ConsultarCobrancas/SolicitacaoCobrancaSwalHtml.jsx";
import ConsultarCobrancasDataTable from "./ConsultarCobrancas/ConsultarCobrancasDataTable.jsx"

function ConsultarCobrancas() {
  const [linhas, setLinhas] = React.useState([]);
  const { session } = React.useContext(AppContext);
  const [checkboxesMarcados, setCheckboxesMarcados] = React.useState([]);
  const [informarPagamentoButton, setInformarPagamentoButton] =
    React.useState(false);
  const [enviarEmailModal, setEnviarEmailModal] = React.useState(false);
  const [downloadEmMassaModal, setDownloadEmMassaModal] = React.useState(false);
  const [informarPagamentoModal, setInformarPagamentoModal] =
    React.useState(false);
  const [visualizarDocumentoModal, setVisualizarDocumentoModal] =
    React.useState(false);
  const [filtros, setFiltros] = React.useState({
    status: "TODOS",
    inicio: primeiroDiaDoMes,
    fim: ultimoDiaDoMes,
  });
  const [cards, setCards] = React.useState({});
  const [values, setValues] = React.useState({});

  const getCobrancasCb = React.useCallback(
    (inicio = null, fim = null, status = null) => {
      return CobrancaApiClient.listCobrancas(
        session.empresa.replace(/E/gi, ""),
        {
          inicio: inicio ? inicio : filtros.inicio,
          fim: fim ? fim : filtros.fim,
          status: status
            ? status === "TODAS"
              ? null
              : status
            : filtros.status === "TODAS"
              ? null
              : filtros.status,
        }
      );
    },
    [filtros.fim, filtros.inicio, filtros.status, session.empresa]
  );

  const { request: atualizarCobrancas, data: cobrancasResponse } =
    useApi(getCobrancasCb);
  const solicitarCancelamentoApi = useApi(
    CobrancaApiClient.solicitarCancelamento
  );
  const downloadMassaApi = useApi(CobrancaApiClient.downloadMassa);
  const enviarEmailApi = useApi(CobrancaApiClient.enviarEmail);
  const cancelarPagamentoApi = useApi(CobrancaApiClient.cancelarPagamento);
  const informarPagamentoApi = useApi(CobrancaApiClient.informarPagamento);
  const visualizarCobrancaApi = useApi(CobrancaApiClient.visualizarCobranca);
  const cancelarCobrancaApi = useApi(CobrancaApiClient.cancelarCobranca);

  //Busca cobranças ao renderizar tela
  React.useEffect(() => {
    if (session.empresa) atualizarCobrancas();
  }, [atualizarCobrancas, session.empresa]);

  //Define o retorno da api nos valores das linhas da tabela e dos cards
  React.useEffect(() => {
    if (!cobrancasResponse) return;
    setLinhas(cobrancasResponse.cobrancas);
    setCards({ ...cobrancasResponse });
  }, [cobrancasResponse]);

  const togDownloadEmMassaModal = () => {
    setValues({
      download_boleto: true,
      download_nfse: true,
      opcao_download: true,
    });
    setDownloadEmMassaModal(true);
  };

  const togEnviarEmailModal = (uid, emails) => {
    setValues({
      incluir_boleto: true,
      incluir_nfse: true,
      email_cadastrado: emails,
      cobranca_uid: uid,
    });
    setEnviarEmailModal(true);
  };

  const togInformarPagamentoModal = (uid, valor, valor_pago = null) => {
    setValues({
      valor_pago: valor,
      data_pagamento: hoje.toISOString().split("T")[0],
      cobranca_uid: uid,
    });
    setInformarPagamentoButton(valor_pago);
    setInformarPagamentoModal(!informarPagamentoModal);
  };

  const togVisualizarDocumentoModal = async (uid, tipo) => {
    const response = await visualizarCobrancaApi.request(
      session.empresa.replace(/E/gi, ""),
      {
        uid: uid,
        tipo: tipo,
      }
    );
    if (!response) return;
    const pdfBlob = new Blob([response], { type: "application/pdf" });
    const documentUrl = URL.createObjectURL(pdfBlob);

    setValues({ documento: documentUrl });
    setVisualizarDocumentoModal(!visualizarDocumentoModal);
  };

  const downloadEmMassa = async () => {
    if (values.download_boleto === false && values.download_nfse === false)
      return ErrorSwal({
        text: "Selecione pelo menos um tipo de arquivo.",
      });

    const formData = new FormData();

    if (values.opcao_download) {
      if (checkboxesMarcados.length === 0)
        return ErrorSwal({
          text: "Nenhuma cobrança marcada.",
        });

      formData.append("ids", JSON.stringify(checkboxesMarcados));
    } else {
      const idsParaAppend = linhas.map((linha) => linha.uid);

      if (idsParaAppend.length === 0) {
        return ErrorSwal({
          text: "Não existem cobranças para realizar download.",
        });
      }

      formData.append("ids", JSON.stringify(idsParaAppend));
    }

    formData.append("boleto", values.download_boleto);
    formData.append("nfse", values.download_nfse);

    const response = await downloadMassaApi.request(
      session.empresa.replace(/E/gi, ""),
      formData
    );
    if (!response) return;
    const blob = new Blob([response]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Cobrancas - ${hoje
      .toLocaleDateString("pt-BR")
      .replace(/\//g, "-")}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    setDownloadEmMassaModal(false);
  };

  const enviarCobrancaEmail = () => {
    OptionsSwal({
      title: "Você tem certeza que deseja enviar este e-mail de cobrança?",
      icon: "warning",
    }).then(async (result) => {
      if (result.isConfirmed) {
        if (values.incluir_boleto === false && values.incluir_nfse === false)
          return ErrorSwal({
            text: "Não é possível enviar um e-mail de cobrança sem selecionar pelo menos um tipo de arquivo.",
          });

        const formData = new FormData();
        formData.append("email", values.email_cadastrado);
        formData.append("boleto", values.incluir_boleto);
        formData.append("nfse", values.incluir_nfse);

        if (values.email_cadastrado === "")
          return ErrorSwal({
            text: "Nenhum email informado.",
          });

        const emails = values.email_cadastrado;
        const erros = Validation.validarEmails(emails);
        if (erros)
          return ErrorSwal({
            text: "Por favor, forneça email(s) válido(s).",
          });

        const response = await enviarEmailApi.request(
          session.empresa.replace(/E/gi, ""),
          values.cobranca_uid,
          formData
        );
        if (!response) return;
        CommonSwal({
          title: "E-mail(s) enviado(s) com sucesso!",
          icon: "success",
        }).then(async () => {
          setEnviarEmailModal(false);
          await atualizarCobrancas();
        });
      }
    });
  };

  const cancelarPagamento = async () => {
    const response = await cancelarPagamentoApi.request(
      session.empresa.replace(/E/gi, ""),
      values.cobranca_uid
    );
    if (!response) return;
    CommonSwal({
      title: "Pagamento cancelado com sucesso!",
      icon: "success",
    }).then(async () => {
      setInformarPagamentoModal(false);
      await atualizarCobrancas();
    });
  };

  const efetuarPagamento = async () => {
    const formData = new FormData();
    formData.append("valor_pago", values.valor_pago);
    formData.append("data_pagamento", values.data_pagamento);

    if (Validation.verificarForm(formData))
      return ErrorSwal({
        text: "Favor preencher todos os dados obrigatórios.",
      });

    const response = await informarPagamentoApi.request(
      session.empresa.replace(/E/gi, ""),
      values.cobranca_uid,
      formData
    );
    if (!response) return;
    CommonSwal({
      title: "Pagamento efetuado com sucesso!",
      icon: "success",
    }).then(async () => {
      setInformarPagamentoModal(false);
      await atualizarCobrancas();
    });
  };

  const solicitarCancelamento = (uid) => {
    OptionsSwal({
      title: "Você deseja solicitar o cancelamento dessa cobrança?",
      icon: "warning",
      html: <SolicitacaoCobrancaSwalHtml />,
      preConfirm: () => [document.querySelector("#justificativa").value],
    }).then(async (result) => {
      if (result.isConfirmed) {
        const opcaoSelecionada = parseInt(
          result.value[0].toString().replace(/"/g, "")
        );
        const formData = new FormData();
        formData.append("justificativa", opcaoSelecionada);
        if (Validation.verificarForm(formData)) {
          return ErrorSwal({
            text: "Favor preencher todos os dados obrigatórios.",
          });
        }
        const response = await solicitarCancelamentoApi.request(
          session.empresa.replace(/E/gi, ""),
          uid,
          formData
        );
        if (!response) return;
        CommonSwal({
          title: "Solicitação de cancelamento realizado com sucesso!",
          icon: "success",
        }).then(async () => {
          setInformarPagamentoModal(false);
          await atualizarCobrancas();
        });
      }
    });
  };

  const cancelarCobranca = async (uid) => {
    const response = await cancelarCobrancaApi.request(
      session.empresa.replace(/E/gi, ""),
      uid
    );
    if (!response) return;
    CommonSwal({
      title: "Cobrança cancelada!",
      icon: "success",
    }).then(async () => {
      await atualizarCobrancas();
    });
  };

  return (
    <>
      <div className="row">
        <ConsultarCobrancasCards cards={cards} />
      </div>
      <Card>
        <div className="row">
          <ConsultarCobrancasFiltros filtrosHook={{ filtros, setFiltros }} />
        </div>
        <br />
        <div className="row">
          <ConsultarCobrancasDataTable
            linhas={linhas}
            checkboxesHook={{ checkboxesMarcados, setCheckboxesMarcados }}
            togDownloadEmMassaModal={togDownloadEmMassaModal}
            togEnviarEmailModal={togEnviarEmailModal}
            togInformarPagamentoModal={togInformarPagamentoModal}
            togVisualizarDocumentoModal={togVisualizarDocumentoModal}
            solicitarCancelamento={solicitarCancelamento}
            cancelarCobranca={cancelarCobranca}
          />
        </div>
      </Card>
      <DownloadMassaModal
        DownloadModalHook={{ downloadEmMassaModal, setDownloadEmMassaModal }}
        valuesHook={{ values, setValues }}
        downloadEmMassa={downloadEmMassa}
      />
      <EnviarEmailModal
        emailModalHook={{ enviarEmailModal, setEnviarEmailModal }}
        valuesHook={{ values, setValues }}
        enviarCobrancaEmail={enviarCobrancaEmail}
      />
      <InformarPagamentoModal
        pagamentoModalHook={{
          informarPagamentoModal,
          setInformarPagamentoModal,
        }}
        valuesHook={{ values, setValues }}
        informarPagamentoButton={informarPagamentoButton}
        cancelarPagamento={cancelarPagamento}
        efetuarPagamento={efetuarPagamento}
      />
      <VisualizarCobrancaModal
        visualizarModalHook={{
          visualizarDocumentoModal,
          setVisualizarDocumentoModal,
        }}
        documento={values.documento}
      />
    </>
  );
}

export default ConsultarCobrancas;
